/**
 * LandingReferencePill — the queued-upload chip shown in the landing prompt
 * composer (thumbnail/preview + filename + status + remove button).
 */
import { useTranslation } from 'react-i18next'
import { AudioLines, ImageIcon, Loader2, Video, X } from 'lucide-react'
import type { LandingUploadReference } from './landingUploads'

function LandingReferencePreview({ upload }: { upload: LandingUploadReference }) {
  if (upload.type === 'image') {
    return (
      <img
        src={upload.previewUrl}
        alt=""
        className="h-full w-full object-cover"
        draggable={false}
      />
    )
  }

  if (upload.type === 'video') {
    return (
      <div className="flex h-full w-full items-center justify-center bg-surface-2 text-muted-foreground">
        <Video className="h-4 w-4" strokeWidth={2} />
      </div>
    )
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-surface-2 text-muted-foreground">
      <AudioLines className="h-4 w-4" strokeWidth={2} />
    </div>
  )
}

export function LandingReferencePill({
  upload,
  onRemove,
}: {
  upload: LandingUploadReference
  onRemove?: (id: string) => void
}) {
  const { t } = useTranslation('landing')
  const isBusy = upload.status === 'uploading'
  const isError = upload.status === 'error'
  const isUploaded = upload.status === 'uploaded'
  const Icon = upload.type === 'image' ? ImageIcon : upload.type === 'video' ? Video : AudioLines

  return (
    <div
      data-testid={`landing-reference-${upload.id}`}
      className={`group relative flex h-12 min-w-0 max-w-[180px] items-center gap-2 rounded-lg border px-2 transition-colors ${
        isError
          ? 'border-destructive/60 bg-destructive/10'
          : isUploaded
            ? 'border-input-border-hover bg-surface-1'
            : 'border-input-border bg-surface-1/70'
      }`}
      title={upload.error || upload.name}
    >
      <div className={`relative h-8 w-8 flex-shrink-0 overflow-hidden rounded-md border border-border-subtle ${
        isUploaded ? '' : 'grayscale opacity-70'
      }`}>
        <LandingReferencePreview upload={upload} />
        {isBusy && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/65">
            <Loader2 className="h-4 w-4 animate-spin text-foreground" strokeWidth={2} />
          </div>
        )}
      </div>
      <div className="min-w-0 text-left">
        <div className="flex min-w-0 items-center gap-1 text-xs font-medium text-foreground">
          <Icon className="h-3 w-3 flex-shrink-0 text-muted-foreground" strokeWidth={2} />
          <span className="truncate">{upload.name}</span>
        </div>
        <div className={`truncate text-xxs ${isError ? 'text-destructive' : 'text-text-muted'}`}>
          {isError ? upload.error : upload.status}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onRemove?.(upload.id)}
        disabled={isBusy}
        className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-input-border bg-background text-muted-foreground opacity-0 shadow transition-opacity hover:border-input-border-hover hover:bg-surface-2 hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none group-hover:opacity-100 group-focus-within:opacity-100"
        aria-label={t('hero.removeUpload', { name: upload.name })}
        data-testid={`landing-reference-remove-${upload.id}`}
      >
        <X className="h-3 w-3" strokeWidth={2} />
      </button>
    </div>
  )
}
